import { useQuery } from '@tanstack/react-query';
import {
  Briefcase,
  DollarSign,
  CheckSquare,
  TrendingUp,
  Calendar,
  Share2,
  ArrowRight,
  Clock,
  Linkedin,
  Facebook,
  Instagram,
  Twitter,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Card, CardHeader, CardTitle, CardContent } from '../components/Card';
import { StatCard } from '../components/StatCard';
import { Badge, getStatusVariant, getPlatformStyles } from '../components/Badge';
import { dealsApi, tasksApi, socialPostsApi } from '../lib/api';
import { format, isToday, isTomorrow, isPast } from 'date-fns';
import type { Deal, Task, SocialPost, DealStage } from '../types';

// Format currency
const formatCurrency = (value: number | null | undefined) => {
  if (!value) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Format compact currency for large numbers
const formatCompactCurrency = (value: number | null | undefined) => {
  if (!value) return '$0';
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return formatCurrency(value);
};

// Deal stages for pipeline (matches D1 schema)
const DEAL_STAGES: DealStage[] = ['discovery', 'qualification', 'proposal', 'negotiation', 'won'];

const stageColors: Record<DealStage, string> = {
  discovery: 'bg-[var(--asi-info)]',
  qualification: 'bg-[var(--asi-warning)]',
  proposal: 'bg-[#8B5CF6]',
  negotiation: 'bg-[var(--asi-accent)]',
  won: 'bg-[var(--asi-success)]',
  lost: 'bg-[var(--asi-error)]',
};

// Platform icons
const PlatformIcon = ({ platform }: { platform: string }) => {
  const iconClass = 'w-4 h-4';
  switch (platform) {
    case 'linkedin':
      return <Linkedin className={iconClass} />;
    case 'facebook':
      return <Facebook className={iconClass} />;
    case 'instagram':
      return <Instagram className={iconClass} />;
    case 'twitter':
      return <Twitter className={iconClass} />;
    default:
      return <Share2 className={iconClass} />;
  }
};

// Due date label component
const DueDateLabel = ({ date }: { date: string | null }) => {
  if (!date) return <span className="text-[var(--asi-text-muted)]">No due date</span>;

  const dateObj = new Date(date);
  if (isToday(dateObj)) {
    return <span className="text-[var(--asi-warning)] font-medium">Today</span>;
  }
  if (isTomorrow(dateObj)) {
    return <span className="text-[var(--asi-info)] font-medium">Tomorrow</span>;
  }
  if (isPast(dateObj)) {
    return <span className="text-[var(--asi-error)] font-medium">Overdue</span>;
  }
  return <span className="text-[var(--asi-text-secondary)]">{format(dateObj, 'MMM d')}</span>;
};

export function Dashboard() {
  // Fetch dashboard data
  const { data: deals, isLoading: dealsLoading } = useQuery({
    queryKey: ['deals'],
    queryFn: () => dealsApi.list({ limit: 100 }),
  });

  const { data: tasks, isLoading: tasksLoading } = useQuery({
    queryKey: ['tasks', 'pending'],
    queryFn: () => tasksApi.list({ status: 'pending', limit: 10 }),
  });

  const { data: scheduledPosts, isLoading: postsLoading } = useQuery({
    queryKey: ['social-posts', 'scheduled'],
    queryFn: () => socialPostsApi.scheduled(),
  });

  // Calculate stats from deals
  const dealsList = (deals?.deals || []) as Deal[];
  const totalPipelineValue = dealsList
    .filter((d) => d.stage !== 'lost' && d.stage !== 'won')
    .reduce((sum, d) => sum + (d.value || 0), 0);
  const wonDeals = dealsList.filter((d) => d.stage === 'won');
  const wonValue = wonDeals.reduce((sum, d) => sum + (d.value || 0), 0);
  const activeDeals = dealsList.filter((d) => !['won', 'lost'].includes(d.stage));

  // Group deals by stage
  const dealsByStage = DEAL_STAGES.reduce((acc, stage) => {
    acc[stage] = dealsList.filter((d) => d.stage === stage);
    return acc;
  }, {} as Record<DealStage, Deal[]>);

  const tasksList = (tasks?.tasks || []) as Task[];
  const scheduledPostsList = (scheduledPosts?.posts || []) as SocialPost[];

  const isLoading = dealsLoading || tasksLoading || postsLoading;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="animate-fade-in-up">
        <h1 className="text-2xl font-semibold text-[var(--asi-text)] tracking-tight">
          Dashboard
        </h1>
        <p className="text-[var(--asi-text-secondary)] mt-1">
          Welcome back! Here's your sales overview.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="animate-fade-in-up delay-75">
          <StatCard
            title="Pipeline Value"
            value={formatCompactCurrency(totalPipelineValue)}
            subtitle={`${activeDeals.length} active deals`}
            icon={<DollarSign className="w-5 h-5" />}
            variant="accent"
          />
        </div>
        <div className="animate-fade-in-up delay-150">
          <StatCard
            title="Won Deals"
            value={wonDeals.length}
            subtitle={formatCurrency(wonValue)}
            icon={<TrendingUp className="w-5 h-5" />}
          />
        </div>
        <div className="animate-fade-in-up delay-225">
          <StatCard
            title="Pending Tasks"
            value={tasksList.length}
            subtitle="Requires attention"
            icon={<CheckSquare className="w-5 h-5" />}
          />
        </div>
        <div className="animate-fade-in-up delay-300">
          <StatCard
            title="Scheduled Posts"
            value={scheduledPostsList.length}
            subtitle="Ready to publish"
            icon={<Share2 className="w-5 h-5" />}
          />
        </div>
      </div>

      {/* Pipeline Overview */}
      <Card className="animate-fade-in-up">
        <CardHeader className="flex items-center justify-between">
          <CardTitle>Pipeline Overview</CardTitle>
          <Link
            to="/pipeline"
            className="inline-flex items-center gap-1 text-sm font-medium text-[var(--asi-text-secondary)] hover:text-[var(--asi-text)] transition-colors"
          >
            View All
            <ArrowRight className="w-4 h-4" />
          </Link>
        </CardHeader>
        <CardContent padding="none">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
            {DEAL_STAGES.map((stage, idx) => {
              const stageDeals = dealsByStage[stage] || [];
              const stageValue = stageDeals.reduce((sum, d) => sum + (d.value || 0), 0);
              const isLast = idx === DEAL_STAGES.length - 1;

              return (
                <div
                  key={stage}
                  className={`p-5 text-center ${
                    !isLast ? 'border-r border-[var(--asi-bg-tertiary)]' : ''
                  } hover:bg-[var(--asi-bg-secondary)] transition-colors`}
                >
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <span
                      className={`w-2 h-2 rounded-full ${stageColors[stage]}`}
                    />
                    <p className="text-xs font-medium text-[var(--asi-text-secondary)] uppercase tracking-wider">
                      {stage.replace('_', ' ')}
                    </p>
                  </div>
                  <p className="text-2xl font-semibold text-[var(--asi-text)] tracking-tight">
                    {stageDeals.length}
                  </p>
                  <p className="text-sm text-[var(--asi-text-muted)] mt-1">
                    {formatCompactCurrency(stageValue)}
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Deals */}
        <Card className="animate-fade-in-up">
          <CardHeader className="flex items-center justify-between">
            <CardTitle>Recent Deals</CardTitle>
            <Badge variant="info" size="sm">
              {dealsList.length} total
            </Badge>
          </CardHeader>
          <CardContent padding="none">
            {isLoading ? (
              <div className="p-8 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-[var(--asi-accent)] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : dealsList.length > 0 ? (
              <div className="divide-y divide-[var(--asi-bg-tertiary)]">
                {dealsList.slice(0, 5).map((deal, idx) => (
                  <div
                    key={deal.id}
                    className="p-4 flex items-center justify-between hover:bg-[var(--asi-bg-secondary)] transition-colors animate-slide-in"
                    style={{ animationDelay: `${idx * 50}ms` }}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center text-[var(--asi-text-light)] font-semibold text-sm ${
                          stageColors[deal.stage] || 'bg-[var(--asi-bg-tertiary)]'
                        }`}
                      >
                        {deal.title?.charAt(0) || 'D'}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-[var(--asi-text)] truncate">
                          {deal.title}
                        </p>
                        <p className="text-sm text-[var(--asi-text-muted)] truncate">
                          {deal.contact?.company || 'No company'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-4">
                      <p className="font-semibold text-[var(--asi-text)]">
                        {formatCurrency(deal.value)}
                      </p>
                      <Badge variant={getStatusVariant(deal.stage)} size="sm">
                        {deal.stage}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-[var(--asi-bg-tertiary)] flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-[var(--asi-text-muted)]" />
                </div>
                <p className="text-[var(--asi-text-secondary)]">No deals yet</p>
                <p className="text-sm text-[var(--asi-text-muted)] mt-1">
                  Create your first deal to get started
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending Tasks */}
        <Card className="animate-fade-in-up">
          <CardHeader className="flex items-center justify-between">
            <CardTitle>Pending Tasks</CardTitle>
            <Badge variant="warning" size="sm" dot>
              {tasksList.length} pending
            </Badge>
          </CardHeader>
          <CardContent padding="none">
            {isLoading ? (
              <div className="p-8 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-[var(--asi-accent)] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : tasksList.length > 0 ? (
              <div className="divide-y divide-[var(--asi-bg-tertiary)]">
                {tasksList.slice(0, 5).map((task, idx) => (
                  <div
                    key={task.id}
                    className="p-4 flex items-center justify-between hover:bg-[var(--asi-bg-secondary)] transition-colors animate-slide-in"
                    style={{ animationDelay: `${idx * 50}ms` }}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-[var(--asi-bg-tertiary)] flex items-center justify-center">
                        <CheckSquare className="w-5 h-5 text-[var(--asi-text-secondary)]" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-[var(--asi-text)] truncate">
                          {task.title}
                        </p>
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="w-3.5 h-3.5 text-[var(--asi-text-muted)]" />
                          <DueDateLabel date={task.due_date} />
                        </div>
                      </div>
                    </div>
                    <Badge variant={getStatusVariant(task.priority)} size="sm">
                      {task.priority}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-[var(--asi-bg-tertiary)] flex items-center justify-center">
                  <CheckSquare className="w-6 h-6 text-[var(--asi-text-muted)]" />
                </div>
                <p className="text-[var(--asi-text-secondary)]">All caught up!</p>
                <p className="text-sm text-[var(--asi-text-muted)] mt-1">
                  No pending tasks at the moment
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Scheduled Social Posts */}
      <Card className="animate-fade-in-up">
        <CardHeader className="flex items-center justify-between">
          <div>
            <CardTitle>Upcoming Social Posts</CardTitle>
            <p className="text-sm text-[var(--asi-text-muted)] mt-1">
              Scheduled content across platforms
            </p>
          </div>
          <Link
            to="/marketing/social"
            className="inline-flex items-center gap-1 text-sm font-medium text-[var(--asi-text-secondary)] hover:text-[var(--asi-text)] transition-colors"
          >
            Manage Posts
            <ArrowRight className="w-4 h-4" />
          </Link>
        </CardHeader>
        <CardContent padding="none">
          {isLoading ? (
            <div className="p-8 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-[var(--asi-accent)] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : scheduledPostsList.length > 0 ? (
            <div className="divide-y divide-[var(--asi-bg-tertiary)]">
              {scheduledPostsList.slice(0, 5).map((post, idx) => (
                <div
                  key={post.id}
                  className="p-4 flex items-center gap-4 hover:bg-[var(--asi-bg-secondary)] transition-colors animate-slide-in"
                  style={{ animationDelay: `${idx * 50}ms` }}
                >
                  <div
                    className={`p-2.5 rounded-xl ${getPlatformStyles(post.platform)}`}
                  >
                    <PlatformIcon platform={post.platform} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[var(--asi-text)] truncate">
                      {post.post_text}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-[var(--asi-text-muted)] mt-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {post.scheduled_for
                        ? format(new Date(post.scheduled_for), 'MMM d, h:mm a')
                        : 'Not scheduled'}
                    </div>
                  </div>
                  <Badge
                    className={getPlatformStyles(post.platform)}
                    size="sm"
                  >
                    {post.platform}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-[var(--asi-bg-tertiary)] flex items-center justify-center">
                <Calendar className="w-6 h-6 text-[var(--asi-text-muted)]" />
              </div>
              <p className="text-[var(--asi-text-secondary)]">No scheduled posts</p>
              <p className="text-sm text-[var(--asi-text-muted)] mt-1">
                Schedule content to maintain consistent engagement
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
